import { useState, useEffect, useMemo } from 'react';
import { Smile, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { workoutApi } from '@/services/api';
import { Card } from '@/components/common';
import type { Workout } from '@/types';

interface MoodDataPoint {
  date: Date;
  mood: number; // 1-5 scale
}

const TIME_RANGES = [
  { label: '7 nap', days: 7 },
  { label: '14 nap', days: 14 },
  { label: '30 nap', days: 30 },
];

const MOOD_LABELS: Record<number, { emoji: string; label: string }> = {
  1: { emoji: '😫', label: 'Fáradt' },
  2: { emoji: '😕', label: 'Gyenge' },
  3: { emoji: '😐', label: 'Átlagos' },
  4: { emoji: '😊', label: 'Jó' },
  5: { emoji: '💪', label: 'Szuper' },
};

export const MoodChart = () => {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(30);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const loadWorkouts = async () => {
      try {
        let allWorkouts: Workout[] = [];
        let cursor: string | undefined;
        let hasMore = true;

        while (hasMore && allWorkouts.length < 100) {
          const response = await workoutApi.getAll(cursor);
          if (response.success && response.data) {
            allWorkouts = [...allWorkouts, ...response.data.items];
            cursor = response.data.nextCursor || undefined;
            hasMore = !!response.data.nextCursor;
          } else {
            hasMore = false;
          }
        }

        setWorkouts(allWorkouts);
      } catch (error) {
        console.error('Failed to load workouts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWorkouts();
  }, []);

  // Calculate mood data from workouts (using endMood)
  const moodData = useMemo(() => {
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - selectedRange * 24 * 60 * 60 * 1000);

    const data: MoodDataPoint[] = workouts
      .filter(w => {
        // Only include completed workouts with endMood
        if (!w.endTime) return false;
        if (!w.endMood) return false;
        if (new Date(w.startTime) < cutoffDate) return false;
        return true;
      })
      .map(w => ({
        date: new Date(w.startTime),
        mood: w.endMood!,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return data;
  }, [workouts, selectedRange]);

  // Calculate stats
  const stats = useMemo(() => {
    if (moodData.length === 0) return null;

    const moods = moodData.map(d => d.mood);
    const first = moods[0];
    const last = moods[moods.length - 1];
    const change = last - first;
    const avg = moods.reduce((a, b) => a + b, 0) / moods.length;

    return { first, last, change, avg };
  }, [moodData]);

  // Generate SVG path for the chart
  const chartPath = useMemo(() => {
    if (moodData.length < 2 || !stats) return null;

    const width = 100;
    const height = 50;
    const padding = 5;

    const minMood = 1;
    const maxMood = 5;
    const moodRange = maxMood - minMood;

    const points = moodData.map((d, i) => {
      const x = padding + (i / (moodData.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((d.mood - minMood) / moodRange) * (height - 2 * padding);
      return { x, y, mood: d.mood };
    });

    const pathD = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ');

    // Area fill path
    const areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

    return { pathD, areaD, points };
  }, [moodData, stats]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  };

  const getMoodInfo = (mood: number) => {
    const rounded = Math.round(mood);
    return MOOD_LABELS[rounded] || MOOD_LABELS[3];
  };

  if (loading) {
    return (
      <Card title="Hangulat">
        <div className="h-48 flex items-center justify-center">
          <div className="inline-block w-6 h-6 border-3 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Card>
    );
  }

  if (moodData.length === 0) {
    return (
      <Card title="Hangulat">
        <div className="h-48 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <Smile className="w-12 h-12 mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">Nincs hangulat adat az elmúlt {selectedRange} napban</p>
          <p className="text-xs mt-1">Edzés befejezésekor add meg a hangulatodat</p>
        </div>
        {/* Time range selector */}
        <div className="mt-4 flex justify-center gap-2">
          {TIME_RANGES.map(range => (
            <button
              key={range.days}
              onClick={() => setSelectedRange(range.days)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                selectedRange === range.days
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title="Hangulat"
      headerActions={
        <div className="flex gap-1">
          {TIME_RANGES.map(range => (
            <button
              key={range.days}
              onClick={() => setSelectedRange(range.days)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${
                selectedRange === range.days
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      }
    >
      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Első</p>
            <div className="flex items-center justify-center gap-1">
              <span className="text-lg">{getMoodInfo(stats.first).emoji}</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{stats.first}/5</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Utolsó</p>
            <div className="flex items-center justify-center gap-1">
              <span className="text-lg">{getMoodInfo(stats.last).emoji}</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{stats.last}/5</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Változás</p>
            <div className={`flex items-center justify-center gap-1 font-semibold ${
              stats.change > 0 ? 'text-green-600 dark:text-green-400' : stats.change < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
            }`}>
              {stats.change > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : stats.change < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <Minus className="w-4 h-4" />
              )}
              <span>{stats.change > 0 ? '+' : ''}{stats.change}</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="relative w-full aspect-[2/1]">
        {chartPath && (
          <svg
            viewBox="0 0 100 50"
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            {/* Grid lines for mood levels */}
            <line x1="5" y1="5" x2="95" y2="5" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="16.25" x2="95" y2="16.25" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="27.5" x2="95" y2="27.5" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="38.75" x2="95" y2="38.75" stroke="#e5e7eb" strokeWidth="0.3" />
            <line x1="5" y1="45" x2="95" y2="45" stroke="#e5e7eb" strokeWidth="0.3" />

            {/* Area fill */}
            <path
              d={chartPath.areaD}
              fill="url(#moodGradient)"
              opacity="0.3"
            />

            {/* Line */}
            <path
              d={chartPath.pathD}
              fill="none"
              stroke="#10b981"
              className="chart-line"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Points with mood-based colors and hover */}
            {chartPath.points.map((p, i) => (
              <g key={i}>
                {/* Invisible larger hit area */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="4"
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
                {/* Visible point */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={hoveredIndex === i ? 2.5 : 1.5}
                  fill={
                    p.mood >= 4 ? '#10b981' : 
                    p.mood >= 3 ? '#eab308' : 
                    '#ef4444'
                  }
                  className="transition-all duration-150 pointer-events-none"
                />
              </g>
            ))}

            {/* Gradient definition */}
            <defs>
              <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        )}

        {/* Y-axis labels */}
        <span className="absolute top-0 left-0 text-xs text-gray-400">5</span>
        <span className="absolute bottom-0 left-0 text-xs text-gray-400">1</span>

        {/* Tooltip */}
        {hoveredIndex !== null && chartPath && (
          <div
            className="absolute bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none z-10 whitespace-nowrap"
            style={{
              left: `${chartPath.points[hoveredIndex].x}%`,
              top: `${chartPath.points[hoveredIndex].y * 2}%`,
              transform: 'translate(-50%, -120%)',
            }}
          >
            <div className="font-semibold flex items-center gap-1">
              <span>{getMoodInfo(moodData[hoveredIndex].mood).emoji}</span>
              <span>{getMoodInfo(moodData[hoveredIndex].mood).label}</span>
            </div>
            <div className="text-gray-300">{formatDate(moodData[hoveredIndex].date)}</div>
          </div>
        )}
      </div>

      {/* X-axis labels */}
      {moodData.length > 0 && (
        <div className="flex justify-between mt-1 text-xs text-gray-400">
          <span>{formatDate(moodData[0].date)}</span>
          <span>{formatDate(moodData[moodData.length - 1].date)}</span>
        </div>
      )}

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full" />
          <span>Jó (4-5)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-yellow-500 rounded-full" />
          <span>Átlagos (3)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full" />
          <span>Gyenge (1-2)</span>
        </div>
      </div>

      {/* Data points count */}
      <p className="text-xs text-gray-400 text-center mt-2">
        Edzés utáni hangulat az elmúlt {selectedRange} napban
      </p>
    </Card>
  );
};
